import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button as CrcButton } from '@crc-evans/ui-components';
import './GenerateMobilistModal.css';

// ── Helpers ──────────────────────────────────────────
const required = (value) => !value && value !== 0;

const useForm = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const setNestedValue = (section, index, field, value) => {
    setValues((prev) => {
      const arr = prev[section] ? [...prev[section]] : [];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [section]: arr };
    });
    setErrors((prev) => ({ ...prev, [`${section}.${index}.${field}`]: '' }));
  };

  const reset = (defaults = {}) => {
    setValues(defaults);
    setErrors({});
  };

  const validate = (numPipelines) => {
    const errs = {};
    if (required(values.projectName))   errs.projectName   = 'Please enter project name';
    if (required(values.projectNumber)) errs.projectNumber = 'Please enter project number';
    if (required(values.createdBy))     errs.createdBy     = 'Please enter creator name';
    if (required(values.numberOfPipelines)) errs.numberOfPipelines = 'Please enter number of pipelines';

    for (let i = 0; i < numPipelines; i++) {
      const p = (values.pipelines || [])[i] || {};
      if (required(p.pipeSize))             errs[`pipelines.${i}.pipeSize`]             = 'Required';
      if (required(p.wallThickness))        errs[`pipelines.${i}.wallThickness`]        = 'Required';
      if (required(p.numberOfFieldJoints))  errs[`pipelines.${i}.numberOfFieldJoints`]  = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return { values, errors, setValue, setNestedValue, reset, validate };
};

// ── Field components ──────────────────────────────────
const Field = ({ label, required: req, error, children }) => (
  <div className="gm-field">
    <label className={`gm-label${req ? ' gm-label-required' : ''}`}>{label}</label>
    {children}
    {error && <span className="gm-error">{error}</span>}
  </div>
);

// ── Main component ─────────────────────────────────────
const GenerateMobilistModal = ({ visible, onCancel, onSubmit }) => {
  const DEFAULTS = { projectType: 'Onshore', shifts: 'Single', numberOfPipelines: 1 };
  const { values, errors, setValue, setNestedValue, reset, validate } = useForm(DEFAULTS);

  useEffect(() => {
    if (visible) reset(DEFAULTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const numPipelines = parseInt(values.numberOfPipelines, 10) || 1;

  const handleSubmit = () => {
    if (validate(numPipelines)) {
      onSubmit(values);
      reset(DEFAULTS);
    }
  };

  const handleCancel = () => {
    reset(DEFAULTS);
    onCancel();
  };

  // Close on overlay click
  const overlayRef = useRef(null);
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) handleCancel();
  };

  if (!visible) return null;

  const modal = (
    <div className="gm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="gm-box">

        {/* Header */}
        <div className="gm-header">
          <h2 className="gm-title">Generate New Mobilist</h2>
          <button className="gm-close" onClick={handleCancel} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="gm-body">

          {/* General Information */}
          <p className="gm-section-heading">General Information</p>

          <div className="gm-row gm-row-3">
            <Field label="Project Name" required error={errors.projectName}>
              <input
                className="gm-input"
                placeholder="Enter Project Name"
                value={values.projectName || ''}
                onChange={(e) => setValue('projectName', e.target.value)}
              />
            </Field>
            <Field label="Project Number" required error={errors.projectNumber}>
              <input
                className="gm-input"
                placeholder="Enter Project Number"
                value={values.projectNumber || ''}
                onChange={(e) => setValue('projectNumber', e.target.value)}
              />
            </Field>
            <Field label="WBS Element">
              <input
                className="gm-input"
                placeholder="e.g., TBC"
                value={values.wbsElement || ''}
                onChange={(e) => setValue('wbsElement', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Project Type" required error={errors.projectType}>
              <div className="gm-radio-group">
                {['Onshore', 'Offshore', 'Spoolbase', 'Landline'].map((opt) => (
                  <label key={opt} className="gm-radio-label">
                    <input
                      type="radio"
                      name="projectType"
                      value={opt}
                      checked={values.projectType === opt}
                      onChange={() => setValue('projectType', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Shifts" required error={errors.shifts}>
              <div className="gm-radio-group">
                {['Single', 'Double'].map((opt) => (
                  <label key={opt} className="gm-radio-label">
                    <input
                      type="radio"
                      name="shifts"
                      value={opt}
                      checked={values.shifts === opt}
                      onChange={() => setValue('shifts', opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </Field>
          </div>

          <div className="gm-row gm-row-3">
            <Field label="Production Days">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter days"
                value={values.productionDays || ''}
                onChange={(e) => setValue('productionDays', e.target.value)}
              />
            </Field>
            <Field label="No. of Stations">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter stations"
                value={values.numberOfStations || ''}
                onChange={(e) => setValue('numberOfStations', e.target.value)}
              />
            </Field>
            <Field label="Total No. of CRCE Personnel">
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                placeholder="Enter personnel count"
                value={values.totalPersonnel || ''}
                onChange={(e) => setValue('totalPersonnel', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Created By" required error={errors.createdBy}>
              <input
                className="gm-input"
                placeholder="Enter Name"
                value={values.createdBy || ''}
                onChange={(e) => setValue('createdBy', e.target.value)}
              />
            </Field>
            <Field label="Number of Pipelines" required error={errors.numberOfPipelines}>
              <input
                className="gm-input gm-input-number"
                type="number"
                min={1}
                max={20}
                placeholder="e.g. 1"
                value={values.numberOfPipelines || ''}
                onChange={(e) => setValue('numberOfPipelines', e.target.value)}
              />
            </Field>
          </div>

          <hr className="gm-divider" />

          {/* Pipeline Details */}
          <p className="gm-section-heading">Pipeline Details</p>

          {Array.from({ length: numPipelines }, (_, i) => {
            const p = (values.pipelines || [])[i] || {};
            return (
              <div key={i} className="gm-pipeline-card">
                <p className="gm-pipeline-title">Pipeline {i + 1} Details</p>

                <div className="gm-row gm-row-3">
                  <Field label="Pipe Size" required error={errors[`pipelines.${i}.pipeSize`]}>
                    <input
                      className="gm-input"
                      placeholder="Enter size"
                      value={p.pipeSize || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'pipeSize', e.target.value)}
                    />
                  </Field>
                  <Field label="Wall Thickness (mm)" required error={errors[`pipelines.${i}.wallThickness`]}>
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 12"
                      value={p.wallThickness || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'wallThickness', e.target.value)}
                    />
                  </Field>
                  <Field label="No. of Field Joints" required error={errors[`pipelines.${i}.numberOfFieldJoints`]}>
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="Enter count"
                      value={p.numberOfFieldJoints || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'numberOfFieldJoints', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="gm-row gm-row-3">
                  <Field label="Factory Coating Type">
                    <input
                      className="gm-input"
                      placeholder="Enter type"
                      value={p.factoryCoatingType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'factoryCoatingType', e.target.value)}
                    />
                  </Field>
                  <Field label="Steel Cutbacks (mm)">
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 50"
                      value={p.steelCutbacks || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'steelCutbacks', e.target.value)}
                    />
                  </Field>
                  <Field label="FJC Type">
                    <input
                      className="gm-input"
                      placeholder="Enter FJC type"
                      value={p.fjcType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'fjcType', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="gm-row gm-row-3">
                  <Field label="Infill Type">
                    <input
                      className="gm-input"
                      placeholder="Enter infill type"
                      value={p.infillType || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillType', e.target.value)}
                    />
                  </Field>
                  <Field label="Infill Cutbacks (mm)">
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 40"
                      value={p.infillCutbacks || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillCutbacks', e.target.value)}
                    />
                  </Field>
                  <Field label="Infill Thickness (mm)">
                    <input
                      className="gm-input gm-input-number"
                      type="number"
                      placeholder="e.g. 5"
                      value={p.infillThickness || ''}
                      onChange={(e) => setNestedValue('pipelines', i, 'infillThickness', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            );
          })}

        </div>

        {/* Footer */}
        <div className="gm-footer">
          <CrcButton label="Cancel" variant="secondary" onClick={handleCancel} size="small" />
          <CrcButton label="Generate" variant="primary" onClick={handleSubmit} size="small" />
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default GenerateMobilistModal;
